<script setup lang="ts">
import type { User } from '@/types'
import { Head, router, usePage } from '@inertiajs/vue3'
import { computed, ref } from 'vue'

import { dashboardLayout } from '@/lib/layouts'
import { adminUserPath, newAdminUserPath } from '@/routes'
import { PermissionLevel } from '@/types/permissions'

defineOptions({
  layout: dashboardLayout,
})

const page = usePage()
const users = computed(() => page.props.users as User[])

const deleteOpen = ref(false)
const deleteUserId = ref<number | null>(null)

function confirmDelete(id: number) {
  deleteUserId.value = id
  deleteOpen.value = true
}

function executeDelete() {
  if (deleteUserId.value) {
    router.delete(adminUserPath(deleteUserId.value), {
      onFinish: () => {
        router.reload({ only: ['users'] })
      },
    })
  }
  deleteOpen.value = false
  deleteUserId.value = null
}
</script>

<template>
  <Head title="Accounts" />

  <UDashboardPanel id="admin-users">
    <template #header>
      <UDashboardNavbar title="Accounts" :ui="{ right: 'gap-3' }">
        <template #leading>
          <UDashboardSidebarCollapse />
        </template>

        <template #right>
          <UButton
            label="New account"
            icon="i-lucide-plus"
            :to="newAdminUserPath()"
          />
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <UTable
        :data="users"
        :columns="[
          { accessorKey: 'name', header: 'Name' },
          { accessorKey: 'email', header: 'Email' },
          { accessorKey: 'permission_level', header: 'Admin' },
          { accessorKey: 'verified', header: 'Verified' },
          { accessorKey: 'id', header: 'Actions' },
        ]"
      >
        <template #permission_level-cell="{ row }">
          <UBadge
            :color="row.original.permission_level === PermissionLevel.Admin ? 'primary' : 'neutral'"
            variant="subtle"
          >
            {{ row.original.permission_level === PermissionLevel.Admin ? 'Yes' : 'No' }}
          </UBadge>
        </template>

        <template #verified-cell="{ row }">
          <UBadge
            :color="row.original.verified ? 'success' : 'warning'"
            variant="subtle"
          >
            {{ row.original.verified ? 'Yes' : 'No' }}
          </UBadge>
        </template>

        <template #id-cell="{ row }">
          <div class="flex gap-1">
            <UButton
              icon="i-lucide-pencil"
              color="warning"
              class="cursor-pointer"
              variant="ghost"
              :to="`/admin/users/${row.original.id}/edit`"
            />
            <UButton
              icon="i-lucide-trash-2"
              color="error"
              class="cursor-pointer"
              variant="ghost"
              @click="confirmDelete(row.original.id)"
            />
          </div>
        </template>
      </UTable>

      <UModal
        v-model:open="deleteOpen"
        title="Delete account"
        description="Are you sure you want to delete this account? This action cannot be undone."
      >
        <template #footer>
          <div class="flex justify-end gap-2">
            <UButton
              color="neutral"
              variant="ghost"
              label="Cancel"
              @click="deleteOpen = false"
            />
            <UButton
              color="error"
              label="Delete"
              icon="i-lucide-trash-2"
              @click="executeDelete"
            />
          </div>
        </template>
      </UModal>
    </template>
  </UDashboardPanel>
</template>
